/**
 * Step 13 — Journey summary — SYNTHESIS variant (Trading Card deck)
 *
 * Spec: specs/onboarding-rebuild.md → Act 3 step 13
 *
 * Mason's lock 2026-05-14: identity card on top, "By the numbers" stat
 * card stacked behind, peeking from bottom-right. Continue advances within
 * the deck (front → back → step 14). Same component re-used at /profile
 * post-launch with real `stats` prop replacing the day-1 defaults.
 *
 * Front card — identity:
 *   - ILLUMINATE header + dynamic ribbon (Member / Premium / Founding Member)
 *   - Name (serif), role · denomination, goal, translation, interests row
 *   - Member since {year} · #XXXX hash badge
 *
 * Back card — by the numbers:
 *   - 12 stats in 2-col × 6-row grid
 *   - Day-1 values populated from onboarding (1 conversation, 1-day streak,
 *     ministry-map count). The rest are em-dashes until the user accumulates.
 *   - Footer: "This card grows with you. Check back any time."
 *
 * Brand-true: dark navy + gold + serif for values + sans uppercase tracked
 * for labels. No clipart, no kitsch.
 */

import { useEffect, useRef, useState } from 'react'
import ContinueButton from '@/components/onboarding/ContinueButton'

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

// "Crafting your card" loading bar — mirrors the step-4 aha-stat
// "Building your picture" pattern Mason locked. Short enough that the user
// doesn't lose interest, long enough to feel like the card is being assembled.
const LOADING_MS = 1400

// Inline lookup maps — keep cross-step dependencies out so step 13 can
// render in isolation. Mirrors source-of-truth in each step's index.jsx.
const ROLE_LABEL = {
  new_believer: 'New Believer',
  growing_christian: 'Growing Christian',
  small_group_leader: 'Small Group Leader',
  pastor_teacher: 'Pastor / Teacher',
  seminary_student: 'Seminary Student',
  other: 'Christian',
}

// 3 identity-stance goals (Mason lock 2026-05-14, replaces prior 6
// use-case options). GOAL_PHRASE → stat-row value; CALLING_TAGLINE →
// italic centerpiece below the role/denom line.
const GOAL_PHRASE = {
  grow_closer: 'Growing closer to God',
  know_what_believe: 'Knowing what I believe',
  lead_others: 'Leading others',
}

const CALLING_TAGLINE = {
  grow_closer: 'Growing closer to God.',
  know_what_believe: 'Knowing what I believe.',
  lead_others: 'Leading others well.',
}

// Mirrors INTERESTS_OPTIONS in steps/12-profile-data/index.jsx (locked
// 2026-05-14). Drift acceptable for display-only labels.
const INTEREST_LABEL = {
  theology: 'Theology',
  apologetics: 'Apologetics',
  prayer: 'Prayer',
  sermon_prep: 'Sermon prep',
  christian_living: 'Christian living',
  marriage_family: 'Marriage & family',
  evangelism: 'Evangelism',
  christian_history: 'Christian history',
  parenting: 'Parenting',
}

// Member ribbon — promotes when subscription_tier flips, jumps to
// "Founding Member" only when the founding-cap eligibility flag is set.
function ribbonLabel(profile) {
  if (profile.is_founding_member) return 'Founding Member'
  const tier = profile.subscription_tier
  if (tier === 'premium' || tier === 'max') return 'Premium'
  return 'Member'
}

// Tiny non-cryptographic name hash for the "member #" badge — purely
// cosmetic, stable across reloads for the same name.
function hashName(s) {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return h
}

/* --------------------------- Stat formatters --------------------------- */
// Single source of truth for empty-state rendering: null/undefined → em-dash;
// real values render as-is. Day-1 defaults (set in main component) supply
// real numbers for the stats the user has already activated during onboarding.
const DASH = '—'

function fmtCount(v, singular, plural) {
  if (v == null) return DASH
  if (singular == null) return String(v)
  return `${v} ${v === 1 ? singular : plural}`
}

function fmtMinutes(v) {
  if (v == null) return DASH
  if (v < 60) return `${v} min`
  const hrs = Math.floor(v / 60)
  const mins = v % 60
  return mins ? `${hrs}h ${mins}m` : `${hrs}h`
}

function fmtText(v) {
  return v || DASH
}

// Date formatter for stats like "First conversation". Accepts ISO date string
// or Date instance; returns "Mon DD, YYYY" or DASH when null/undefined.
function fmtDate(v) {
  if (v == null) return DASH
  const d = v instanceof Date ? v : new Date(v)
  if (Number.isNaN(d.getTime())) return DASH
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/* ------------------------------- StatRow ------------------------------ */
// A single labeled stat row inside the front card.
function StatRow({ label, value, accent = false, fullWidth = false }) {
  return (
    <div className={`text-left ${fullWidth ? 'col-span-2' : ''}`}>
      <p
        className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-1.5"
        style={{
          color: accent ? 'var(--gold)' : 'var(--text-muted-soft)',
          fontFamily: 'var(--font-body)',
        }}
      >
        {label}
      </p>
      <p
        className="text-[16px] sm:text-[17px] leading-snug text-[var(--text-primary)]"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        {value}
      </p>
    </div>
  )
}

/* ------------------------------ StatCell ------------------------------ */
// Compact stat cell for the back card — smaller label, numeric-feeling
// value. Em-dash values render in the muted color so the eye skims past
// to the populated cells.
function StatCell({ label, value }) {
  const isEmpty = value === DASH
  return (
    <div className="text-left">
      <p
        className="text-[9px] uppercase tracking-[0.24em] font-semibold mb-1"
        style={{ color: 'var(--text-muted-soft)', fontFamily: 'var(--font-body)' }}
      >
        {label}
      </p>
      <p
        className="text-[15px] sm:text-[16px] leading-snug"
        style={{
          fontFamily: 'var(--font-heading)',
          color: isEmpty ? 'var(--text-muted-soft)' : 'var(--text-primary)',
        }}
      >
        {value}
      </p>
    </div>
  )
}

/* --------------------------- CardShell -------------------------------- */
// Identical chrome (border, shadow, gradient) for both front + back cards
// so the deck reads as one cohesive object. Children render between the
// top + bottom bands.
function CardShell({
  children,
  ribbon,
  footerLeft,
  footerRight,
  active = true,
  glintActive = false,
  glintKey = 0,
}) {
  return (
    <article
      className={`js-deck-card${active ? ' js-deck-card--active' : ''} rounded-2xl overflow-hidden h-full flex flex-col relative`}
      style={{
        // Two-layer bg — solid base ensures the back card never bleeds
        // through the front card; gold tint sits on top for the warm
        // top-left glow. (Single-gradient approach left a translucent
        // upper-left corner that leaked back-card text through. Bug
        // 2026-05-14.)
        background:
          'linear-gradient(155deg, rgba(212, 168, 67, 0.08) 0%, rgba(212, 168, 67, 0) 35%), var(--bg-surface)',
        border: '1.5px solid var(--gold)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.08), 0 24px 60px -28px rgba(0,0,0,0.6), 0 0 60px -10px rgba(212, 168, 67, 0.25)',
      }}
    >
      {/* Glint sweep — diagonal shine from upper-left across to lower-right.
          Mounts only when glintActive (so the loading bar can hold it back),
          and re-mounts on glintKey change so each card swap replays the
          animation. The article's overflow-hidden clips the bar to the
          card boundary. */}
      {glintActive && (
        <div
          key={glintKey}
          className="absolute inset-0 pointer-events-none overflow-hidden"
          aria-hidden
        >
          <div className="js-glint-bar" />
        </div>
      )}

      {/* Top band — Illuminate logo + ribbon */}
      <div
        className="px-6 py-3 flex items-center justify-between border-b shrink-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(212, 168, 67, 0.08) 0%, rgba(212, 168, 67, 0) 100%)',
          borderColor: 'var(--gold-dim)',
        }}
      >
        <p
          className="text-[10px] uppercase tracking-[0.3em] font-bold"
          style={{ color: 'var(--gold)', fontFamily: 'var(--font-body)' }}
        >
          Illuminate
        </p>
        {ribbon && (
          <p
            className="text-[9px] uppercase tracking-[0.22em]"
            style={{ color: 'var(--text-muted-soft)', fontFamily: 'var(--font-body)' }}
          >
            {ribbon}
          </p>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 px-6 sm:px-8 py-6">{children}</div>

      {/* Bottom band */}
      {(footerLeft || footerRight) && (
        <div
          className="px-6 py-2.5 border-t flex items-center justify-between shrink-0"
          style={{ background: 'rgba(0,0,0,0.15)', borderColor: 'var(--glass-border)' }}
        >
          <p
            className="text-[9px] uppercase tracking-[0.22em]"
            style={{ color: 'var(--text-muted-soft)', fontFamily: 'var(--font-body)' }}
          >
            {footerLeft}
          </p>
          {footerRight && (
            <p
              className="text-[9px] uppercase tracking-[0.22em] font-semibold"
              style={{ color: 'var(--gold-dim)', fontFamily: 'var(--font-body)' }}
            >
              {footerRight}
            </p>
          )}
        </div>
      )}
    </article>
  )
}

/* =========================== Main component ============================ */
export default function JourneySummarySynthesis({
  profile = {},
  ministryMapSeeded,
  stats: providedStats,
  onContinue,
  // skipLoading: skip the "Crafting your card" 1400ms loading bar. Use
  // true when re-viewing the card outside onboarding (e.g., the
  // /account/journey route opens the user's existing card — the loading
  // ceremony is onboarding-only theater).
  skipLoading = false,
  // joinedDate: override the "Joined" front-card date. When omitted,
  // defaults to today (correct for onboarding step 12 — user just signed
  // up today). For /account/journey re-views, pass the user's actual
  // signup date so the card shows their REAL join date instead of today.
  // 2026-05-17: Mason caught this — re-viewing the card showed Joined =
  // today (wrong) instead of the actual signup date.
  joinedDate: joinedDateOverride,
}) {
  const [mounted, setMounted] = useState(false)
  const [cardIndex, setCardIndex] = useState(0) // 0 = front, 1 = back
  const [loadingPct, setLoadingPct] = useState(skipLoading ? 100 : 0)
  const loadingDone = loadingPct >= 100
  const loadingRafRef = useRef(null)

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // "Crafting your card" loading bar — fills 0..100% over LOADING_MS, then
  // hands off to the deck reveal + glint sweep. Mirrors the step-4
  // "Building your picture" pattern. Skipped when `skipLoading` is true
  // (settings re-view doesn't need the onboarding ceremony).
  useEffect(() => {
    if (skipLoading) return
    let cancelled = false
    let startTs = null
    const tick = ts => {
      if (cancelled) return
      if (startTs === null) startTs = ts
      const t = Math.min(1, (ts - startTs) / LOADING_MS)
      setLoadingPct(t * 100)
      if (t < 1) loadingRafRef.current = requestAnimationFrame(tick)
    }
    loadingRafRef.current = requestAnimationFrame(tick)
    return () => {
      cancelled = true
      if (loadingRafRef.current) cancelAnimationFrame(loadingRafRef.current)
    }
  }, [skipLoading])

  const name = profile.first_name || profile.display_name || 'Friend'
  const role = ROLE_LABEL[profile.role] || 'Christian'
  const denom = profile.denomination || 'Non-denominational'
  const goal = GOAL_PHRASE[profile.primary_goal] || 'Growing in faith'
  const calling = CALLING_TAGLINE[profile.primary_goal] || 'Growing in faith.'
  const translation = profile.preferred_translation || 'NKJV'
  const ribbon = ribbonLabel(profile)

  const interestSlugs = Array.isArray(profile.interests) ? profile.interests : []
  const interests = interestSlugs.map(s => INTEREST_LABEL[s] || s).filter(Boolean)
  // Truncated for half-width slot — show first 2 + "+N more" if there are
  // additional ones. Mason 2026-05-15: Interests moved up to the slot
  // Prayer focus used to occupy. Full list lives on the back card if we
  // want to surface later.
  const INTERESTS_VISIBLE = 2
  const interestsDisplay =
    interests.length === 0
      ? DASH
      : interests.length <= INTERESTS_VISIBLE
        ? interests.join(' · ')
        : `${interests.slice(0, INTERESTS_VISIBLE).join(' · ')} · +${interests.length - INTERESTS_VISIBLE} more`

  // Reading rhythm — formatted from days/wk + min/day captures (steps 3 + 15).
  // Falls back to a friendly placeholder when neither is set so the row
  // never reads as broken/missing data.
  const rhythmDays = profile.bible_reading_days_per_week
  const rhythmMins = profile.daily_time_goal
  const rhythmDisplay =
    rhythmDays != null
      ? `${rhythmDays} ${rhythmDays === 1 ? 'day' : 'days'} / wk${rhythmMins ? ` · ${rhythmMins} min` : ''}`
      : 'Building a rhythm'

  // Onboarding step 12 (default) shows today's date — user just signed up.
  // /account/journey passes joinedDateOverride from the user's real signup
  // date (derived from stats.dayOfJourney) so re-viewers see their actual
  // join date instead of today.
  const joinedDateSource = joinedDateOverride instanceof Date ? joinedDateOverride : new Date()
  const joinedDate = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(joinedDateSource)
  const memberHash = String(Math.abs(hashName(name)) % 10000).padStart(4, '0')

  // Day-1 defaults assume this is the FIRST view of the card during
  // onboarding. Conversations = 1 (step 8 ai-demo), streak = 1 (started
  // today), ministry map = seeded count, voice = default. Pass `stats`
  // explicitly when re-using on /profile to override.
  // 2026-05-14: dropped firstConversation (redundant with Joined date on
  // front card); added dayOfJourney (pure-growth counter, increments daily).
  const day1Stats = {
    dayOfJourney: 1,
    longestStreak: 1,
    currentStreak: 1,
    conversations: 1,
    timeInWord: null,
    favoriteBook: null,
    topTopic: null,
    mostUsedVoice: 'Default',
    versesSaved: null,
    sermonsPrepped: null,
    bibleStudies: null,
    ministryMapPeople: ministryMapSeeded ? 1 : null,
  }
  const stats = providedStats || day1Stats

  // Continue advances within the deck on first tap, then exits.
  // (front → back → onContinue. Bidirectional flipping uses tap-card or
  // tap-dot, not Continue — Continue is forward-only.)
  const LAST_INDEX = 1
  function handleContinue() {
    if (cardIndex < LAST_INDEX) {
      setCardIndex(i => i + 1)
    } else {
      onContinue?.()
    }
  }

  // Tap-card flip — toggles between front (0) and back (1). Lives alongside
  // the deck-dot controls below; gives the user a third way to navigate
  // (Continue forward / dot direct / card-tap toggle). Mason 2026-05-17:
  // explicit "click the card to go between them."
  function flipCard() {
    setCardIndex(i => (i === 0 ? 1 : 0))
  }

  // Card position transforms — non-active card peeks bottom-right by 12px
  // and dims to 0.55 opacity so the eye locks on the active card. Both
  // cards animate simultaneously on swap; z-index switches instantly so
  // there's no flicker (the offset + dim mask any pop).
  const frontActive = cardIndex === 0
  const backActive = cardIndex === 1

  const frontStyle = {
    position: 'absolute',
    inset: 0,
    transform: frontActive ? 'translate(0, 0)' : 'translate(-12px, -12px)',
    zIndex: frontActive ? 20 : 10,
    opacity: frontActive ? 1 : 0.55,
    transition: `transform 600ms ${EASE}, opacity 400ms ${EASE}`,
  }

  const backStyle = {
    position: 'absolute',
    inset: 0,
    transform: backActive ? 'translate(0, 0)' : 'translate(12px, 12px)',
    zIndex: backActive ? 20 : 10,
    opacity: backActive ? 1 : 0.55,
    transition: `transform 600ms ${EASE}, opacity 400ms ${EASE}`,
  }

  return (
    <div className="w-full max-w-md md:max-w-xl lg:max-w-2xl mx-auto px-5 py-10 sm:py-14">
      <style>{`
        @keyframes jsRuleDraw {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes jsLeadFadeIn {
          0% { opacity: 0; transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        /* Glint sweep — diagonal shine starts off-screen left, slides
           across the card, exits off-screen right. Skewed bar reads as
           "light catching the surface." Plays once per mount; remount
           via key prop on cardIndex change replays it. */
        @keyframes jsGlintSweep {
          0% { left: -40%; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { left: 130%; opacity: 0; }
        }
        .js-glint-bar {
          position: absolute;
          top: -30%;
          bottom: -30%;
          width: 36%;
          left: -40%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.06) 30%,
            rgba(255, 240, 200, 0.22) 50%,
            rgba(255, 255, 255, 0.06) 70%,
            transparent 100%
          );
          transform: rotate(18deg);
          animation: jsGlintSweep 1100ms ${EASE} forwards;
          mix-blend-mode: screen;
        }
        @media (prefers-reduced-motion: reduce) {
          .js-glint-bar { animation: none; opacity: 0; }
        }
        /* Card hover — only the active card responds. Slight scale + a
           stronger gold halo so the card feels tactile under cursor.
           Composes with the wrapper's translate transform — they're on
           separate elements so they don't conflict. */
        .js-deck-card {
          transition: transform 260ms ${EASE}, box-shadow 280ms ${EASE},
                      border-color 220ms ${EASE};
          transform-origin: center center;
        }
        .js-deck-card--active:hover {
          transform: scale(1.015);
          border-color: var(--gold);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.12),
            0 28px 70px -28px rgba(0,0,0,0.65),
            0 0 90px -8px rgba(212, 168, 67, 0.45);
        }
        @media (prefers-reduced-motion: reduce) {
          .js-deck-card { transition: none; }
          .js-deck-card--active:hover { transform: none; }
        }
      `}</style>

      {/* Lead — swaps between loading bar (during craft) and the lead label
          ("Your card" / "By the numbers") once the card is revealed. min-h
          reserves space so the layout doesn't jump on swap. */}
      <div className="text-center mb-4 min-h-[52px] flex items-center justify-center">
        {!loadingDone ? (
          <div className="w-full max-w-[20rem]">
            <p
              className="text-[10px] uppercase tracking-[0.32em] text-[var(--gold)] font-semibold"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Crafting your card
            </p>
            <div
              className="mt-2.5 h-[3px] rounded-full overflow-hidden mx-auto"
              style={{ background: 'var(--bg-surface-high)' }}
              role="progressbar"
              aria-valuenow={Math.round(loadingPct)}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${loadingPct}%`,
                  background: 'linear-gradient(90deg, var(--gold-dim) 0%, var(--gold) 100%)',
                  transition: 'width 90ms linear',
                }}
              />
            </div>
          </div>
        ) : (
          <p
            className="text-[10px] uppercase tracking-[0.32em] text-[var(--gold)] font-semibold"
            style={{
              fontFamily: 'var(--font-body)',
              animation: `jsLeadFadeIn 500ms ${EASE} both`,
            }}
          >
            {cardIndex === 0 ? 'Your card' : 'By the numbers'}
          </p>
        )}
      </div>

      {/* THE DECK — relative wrapper sized for the taller card; 12px right
          + bottom padding makes room for the peek. Hidden until the loading
          bar fills, then fades + scales in. */}
      <div
        className="relative mx-auto"
        style={{
          minHeight: 540,
          paddingRight: 12,
          paddingBottom: 12,
          opacity: loadingDone && mounted ? 1 : 0,
          transform:
            loadingDone && mounted ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.96)',
          transition: `opacity 600ms ${EASE}, transform 700ms ${EASE}`,
        }}
      >
        {/* FRONT CARD — identity */}
        <div
          style={{ ...frontStyle, cursor: 'pointer' }}
          onClick={flipCard}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              flipCard()
            }
          }}
          role="button"
          tabIndex={frontActive ? 0 : -1}
          aria-label="Flip to back of card"
        >
          <CardShell
            ribbon={ribbon}
            footerLeft={`Member · #${memberHash}`}
            active={frontActive}
            glintActive={loadingDone && frontActive}
            glintKey={`front-${cardIndex}`}
          >
            {/* Name + role/denomination + calling tagline. Mason lock
                2026-05-14: bigger name (38-44px), italic calling line in
                the breathing space between role and the gold rule. The
                calling synthesizes goal into a one-line motto. */}
            <header>
              <h1
                className="text-[36px] sm:text-[42px] leading-[0.98] text-[var(--text-primary)]"
                style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.018em' }}
              >
                {name}.
              </h1>
              <p
                className="mt-2.5 text-[10px] uppercase tracking-[0.28em] font-semibold"
                style={{ color: 'var(--gold)', fontFamily: 'var(--font-body)' }}
              >
                {role} <span className="text-[var(--gold-dim)] mx-1">·</span> {denom}
              </p>
              <p
                className="mt-3 italic text-[15px] sm:text-[16px] leading-[1.4]"
                style={{
                  fontFamily: 'var(--font-heading)',
                  color: 'var(--gold-dim)',
                }}
              >
                &ldquo;{calling}&rdquo;
              </p>
            </header>

            {/* Gold rule — animated draw */}
            <div
              aria-hidden
              className="h-px w-full origin-left mt-6"
              style={{
                background:
                  'linear-gradient(90deg, var(--gold) 0%, var(--gold-dim) 60%, transparent 100%)',
                animation: mounted ? `jsRuleDraw 800ms ${EASE} 280ms both` : 'none',
              }}
            />

            {/* Stats grid — 2-col, 3 rows. Interests sits where Prayer focus
                used to (truncated for half-width). Joined is half-width;
                bottom-right slot left empty for breathing room. Bigger
                gap-y to fill the deck height. Mason flag 2026-05-15. */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-7 mt-7">
              <StatRow label="Goal" value={goal} accent />
              <StatRow label="Translation" value={translation} />
              <StatRow label="Rhythm" value={rhythmDisplay} />
              <StatRow label="Interests" value={interestsDisplay} />
              <StatRow label="Joined" value={joinedDate} />
            </div>
          </CardShell>
        </div>

        {/* BACK CARD — by the numbers */}
        <div
          style={{ ...backStyle, cursor: 'pointer' }}
          onClick={flipCard}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              flipCard()
            }
          }}
          role="button"
          tabIndex={backActive ? 0 : -1}
          aria-label="Flip to front of card"
        >
          <CardShell
            ribbon={ribbon}
            footerLeft="This card grows with you"
            active={backActive}
            glintActive={loadingDone && backActive}
            glintKey={`back-${cardIndex}`}
          >
            {/* Caption */}
            <p
              className="text-center text-[10px] uppercase tracking-[0.32em] font-semibold mb-5"
              style={{ color: 'var(--gold)', fontFamily: 'var(--font-body)' }}
            >
              By the numbers
            </p>

            {/* 12-stat grid — 2 col × 6 row. Filled values up top, dashes
                trail down so the eye reads "I have data!" first. */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <StatCell label="First conversation" value={fmtDate(stats.firstConversation)} />
              <StatCell label="Most-used voice" value={fmtText(stats.mostUsedVoice)} />

              <StatCell
                label="Longest streak"
                value={fmtCount(stats.longestStreak, 'day', 'days')}
              />
              <StatCell
                label="Current streak"
                value={fmtCount(stats.currentStreak, 'day', 'days')}
              />

              <StatCell label="Conversations" value={fmtCount(stats.conversations)} />
              <StatCell
                label="Ministry map"
                value={fmtCount(stats.ministryMapPeople, 'person', 'people')}
              />

              <StatCell label="Time in the Word" value={fmtMinutes(stats.timeInWord)} />
              <StatCell label="Verses saved" value={fmtCount(stats.versesSaved)} />

              <StatCell label="Favorite book" value={fmtText(stats.favoriteBook)} />
              <StatCell label="Top topic" value={fmtText(stats.topTopic)} />

              <StatCell label="Sermons prepped" value={fmtCount(stats.sermonsPrepped)} />
              <StatCell label="Bible studies" value={fmtCount(stats.bibleStudies)} />
            </div>
          </CardShell>
        </div>
      </div>

      {/* Deck dots — visual cue that there's more than one card */}
      <div className="flex justify-center gap-1.5 mt-5" aria-hidden="true">
        {[0, 1].map(i => (
          <button
            key={i}
            type="button"
            onClick={() => setCardIndex(i)}
            className="block h-1.5 rounded-full transition-all"
            style={{
              width: i === cardIndex ? 18 : 6,
              background: i === cardIndex ? 'var(--gold)' : 'var(--bg-surface-high)',
              cursor: 'pointer',
            }}
            aria-label={`Show card ${i + 1}`}
          />
        ))}
      </div>

      {/* CTA */}
      <div
        className="mt-6 flex justify-center"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(6px)',
          transition: `opacity 600ms ${EASE} 700ms, transform 600ms ${EASE} 700ms`,
        }}
      >
        <ContinueButton onClick={handleContinue}>Continue</ContinueButton>
      </div>
    </div>
  )
}
